import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/core/migration/add';
import { Stack, Box, Text } from '@atlaskit/primitives';
import { useBooleanFeatureFlag } from '@databases/feature-flags/hooks';
import { defaultStoreUtils } from '@databases/redux';
import {
  ViewChangeType,
  ViewChangeTypes,
  ViewFilterChangeType,
} from '@databases/utils/database/view/view';
import React, { Fragment, FunctionComponent, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { useFilterSettings } from '../../hooks/useFilterSettings';
import useView from '../../hooks/useView';
import { messages } from '../../i18n';

import ViewSettingsFilterRow from './ViewSettingsFilterRow';
import { ViewSettingsFilterRule } from './ViewSettingsFilterRule';
import { ViewSettingsFilterRuleEdit } from './ViewSettingsFilterRuleEdit';
import ViewSettingsFilterUsagePicker from './ViewSettingsFilterUsagePicker';

export interface ViewSettingsFilterProps {
  contentId: string;
  autoFocusLastFilterValue?: boolean;
}

const ViewSettingsFilter: FunctionComponent<ViewSettingsFilterProps> = ({
  contentId,
  autoFocusLastFilterValue,
}) => {
  const { view } = useView();
  const { formatMessage } = useIntl();
  const filterObject = view.settings.filter;
  const store = defaultStoreUtils.useStore();
  const { setViewChange } = useView();
  const isNewFilterDesignEnabled = useBooleanFeatureFlag(
    'databases-frontend-refreshed-filters-panel'
  );

  const {
    draftFilterIds,
    filters,
    handleAddDraftFilter,
    handleChangeFilterType,
    handleCreateFilter,
    handleCreateDefaultFilter,
    handleRemoveDraftFilter,
  } = useFilterSettings({
    onChange: setViewChange,
    filterObject,
    contentId,
    store,
  });

  useEffect(() => {
    // only want to add a filter on the initial render if there are no filters
    if (filters.length === 0) {
      isNewFilterDesignEnabled ? handleAddDraftFilter() : handleCreateDefaultFilter();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(
    () =>
      filters.map((filter, index) => {
        const isLastFilter = index === filters.length - 1;
        const hasDraftFilters = draftFilterIds.length > 0;
        const renderFilterUsagePicker =
          !isLastFilter || (isLastFilter && hasDraftFilters);
        return (
          <Stack key={index} space="space.200">
            {isNewFilterDesignEnabled ? (
              <ViewSettingsFilterRule
                contentId={contentId}
                filter={filter}
                onSubmit={(filter) => {
                  setViewChange({
                    type: ViewChangeTypes.VIEW_FILTERS_SET,
                    filter,
                    path: [index],
                  });
                }}
                onDelete={() => {
                  setViewChange({
                    type: ViewChangeTypes.VIEW_FILTERS_REMOVE,
                    filterId: filter.id,
                    path: [index],
                  });
                }}
              />
            ) : (
              <ViewSettingsFilterRow
                contentId={contentId}
                value={filter}
                autoFocusValue={index === filters.length - 1 && autoFocusLastFilterValue}
                onChange={(change: ViewChangeType) => {
                  if (
                    change.type === ViewFilterChangeType.VIEW_FILTERS_SET_FIELD_ID ||
                    change.type === ViewFilterChangeType.VIEW_FILTERS_SET_FILTER_TYPE ||
                    change.type === ViewFilterChangeType.VIEW_FILTERS_SET_VALUE
                  ) {
                    setViewChange({
                      ...change,
                      path: [index],
                    });
                  }
                }}
                onRemove={() => {
                  setViewChange({
                    type: ViewChangeTypes.VIEW_FILTERS_REMOVE,
                    filterId: filter.id,
                    path: [index],
                  });
                }}
              />
            )}
            {renderFilterUsagePicker && (
              <ViewSettingsFilterUsagePicker
                handleChangeFilterType={handleChangeFilterType}
                isEditable={index === 0}
              />
            )}
          </Stack>
        );
      }),
    [
      autoFocusLastFilterValue,
      contentId,
      draftFilterIds.length,
      filters,
      handleChangeFilterType,
      isNewFilterDesignEnabled,
      setViewChange,
    ]
  );

  const draftRows = draftFilterIds.map((id, index) => (
    <Fragment key={id}>
      <ViewSettingsFilterRuleEdit
        contentId={contentId}
        onCancel={() => handleRemoveDraftFilter(id)}
        onSubmit={(filter) => {
          handleCreateFilter(filter);
          handleRemoveDraftFilter(id);
        }}
      />
      {index !== draftFilterIds.length - 1 && (
        <ViewSettingsFilterUsagePicker handleChangeFilterType={handleChangeFilterType} />
      )}
    </Fragment>
  ));

  return (
    <Stack space="space.200">
      <Text size="small">
        {formatMessage(messages['view-settings-panel-filter.header-info-text'])}
      </Text>
      {(filters.length > 0 || draftRows.length > 0) && (
        <Stack space="space.200">
          {rows}
          {draftRows}
        </Stack>
      )}
      <Box>
        <Button
          testId="view-settings-panel-filter.add-filter-button"
          iconBefore={<AddIcon LEGACY_size="small" label="" />}
          onClick={() =>
            isNewFilterDesignEnabled
              ? handleAddDraftFilter()
              : handleCreateDefaultFilter()
          }
        >
          {formatMessage(messages['view-settings-panel-filter.add-filter-button-text'])}
        </Button>
      </Box>
    </Stack>
  );
};

export default ViewSettingsFilter;
